import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import { HEADER_COLOR, POPPINS } from "./dashboardTypography";

const InsightDataTable = ({ rows, columns, defaultSortBy }) => {
  const [orderBy, setOrderBy] = useState(defaultSortBy || (columns[0] && columns[0].field));
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(columns.map((c) => c.field));

  const sortedRows = useMemo(() => {
    const clone = [...(rows || [])];
    clone.sort((a, b) => {
      const av = a?.[orderBy];
      const bv = b?.[orderBy];
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (av < bv) return order === "asc" ? -1 : 1;
      return order === "asc" ? 1 : -1;
    });
    return clone;
  }, [rows, orderBy, order]);

  const pagedRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );

  const handleSort = (field) => {
    const isAsc = orderBy === field && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(field);
  };

  const activeColumns = columns.filter((column) => visibleColumns.includes(column.field));

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        "& .MuiTableCell-root": {
          ...POPPINS,
          fontSize: "0.8rem",
        },
        "& .MuiTableCell-head": {
          ...POPPINS,
          fontWeight: 600,
          color: HEADER_COLOR,
          fontSize: "0.76rem",
          textTransform: "uppercase",
          letterSpacing: "0.02em",
        },
        "& .MuiTablePagination-root": {
          ...POPPINS,
          fontSize: "0.78rem",
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography variant="subtitle2" sx={{ ...POPPINS, color: HEADER_COLOR, fontWeight: 700 }}>Detailed Performance</Typography>
        <Tooltip title="Column selection">
          <IconButton size="small" onClick={(event) => setAnchorEl(event.currentTarget)}>
            <ViewColumnOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {columns.map((column) => {
          const isVisible = visibleColumns.includes(column.field);
          return (
            <MenuItem
              key={column.field}
              sx={{ ...POPPINS, fontSize: "0.8rem" }}
              onClick={() => {
                setVisibleColumns((prev) =>
                  isVisible ? prev.filter((field) => field !== column.field) : [...prev, column.field]
                );
              }}
            >
              {isVisible ? "Hide" : "Show"} {column.label}
            </MenuItem>
          );
        })}
      </Menu>

      <TableContainer sx={{ maxHeight: 460, overflowX: "auto" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {activeColumns.map((column) => (
                <TableCell key={column.field} align={column.align || "left"}>
                  <TableSortLabel
                    active={orderBy === column.field}
                    direction={orderBy === column.field ? order : "asc"}
                    onClick={() => handleSort(column.field)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.map((row, index) => (
              <TableRow key={`${row.platform || "row"}-${index}`} hover>
                {activeColumns.map((column) => {
                  const raw = row[column.field];
                  const rendered = column.render ? column.render(raw, row) : (raw ?? "N/A");
                  return (
                    <TableCell key={column.field} align={column.align || "left"}>
                      {rendered}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            {pagedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={activeColumns.length}>
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No data available for the selected filters.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </Paper>
  );
};

InsightDataTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      align: PropTypes.string,
      render: PropTypes.func,
    })
  ).isRequired,
  defaultSortBy: PropTypes.string,
};

InsightDataTable.defaultProps = {
  rows: [],
  defaultSortBy: "",
};

export default InsightDataTable;
